import { useMutation } from '@apollo/client';
import { omit, pick } from 'ramda';

import { CreateClientInput } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { refetchInvoiceForm } from '../../../../../../../Invoices/components/FormLayout/hooks/useInvoiceFormQuery';
import { refetchQuoteForm } from '../../../../../../../Quotes/components/FormLayout/hooks/useQuoteFormQuery';
import { refetchGetClients } from '../../../../../../pages/List/hooks/queries/useGetClients';
import { ClientForm } from '../../../../hooks/useClientFormModalContext';
import getFieldsToPickByClientType from '../../helpers/getFieldsToPickByClientType';
import { trimClientName } from '../utils';
import { CreateClientDocument } from './create-client.gql';

const useCreateClient = () => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId is undefined in useCreateClient hook');
  }

  const [mutation, result] = useMutation(CreateClientDocument, {
    awaitRefetchQueries: true,
    refetchQueries: [
      refetchGetClients(companyProfileId),
      refetchInvoiceForm(companyProfileId),
      refetchQuoteForm(companyProfileId),
    ],
  });

  const createClient = (client: ClientForm) => {
    const contactsWithoutIds = client.contacts.map(omit(['clientContactId']));
    const fieldsToPick = getFieldsToPickByClientType(client.type);
    const clientInput = trimClientName(client);
    const input: CreateClientInput = {
      ...pick(fieldsToPick, clientInput),
      companyProfileId,
      contacts: contactsWithoutIds,
    };

    return mutation({ variables: { input } });
  };

  return { createClient, ...result };
};

export default useCreateClient;
